/* eslint-disable react-hooks/set-state-in-effect -- standard initial-load pattern used across admin pages. */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FlaskConical, Plus, Play, Pause, Trash2, Loader2, Trophy,
  Eye, MousePointerClick, ShoppingCart, BarChart3, Pencil,
} from 'lucide-react';

const EMPTY_VARIANT = {
  key: '', name: '', headline: '', subtitle: '', cta_text: '',
  badge_text: '', highlighted_plan_id: '', traffic_pct: 50,
};

function VariantEditor({ variant, onChange, plans }) {
  return (
    <div className="border border-border rounded-lg p-4 space-y-3 bg-white/5">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Key</Label>
          <Input
            value={variant.key}
            onChange={(e) => onChange({ ...variant, key: e.target.value.toUpperCase().slice(0, 3) })}
            placeholder="A"
            className="bg-background border-border"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Nome</Label>
          <Input
            value={variant.name || ''}
            onChange={(e) => onChange({ ...variant, name: e.target.value })}
            placeholder="Original"
            className="bg-background border-border"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Headline (hero.title)</Label>
        <Input
          value={variant.headline || ''}
          onChange={(e) => onChange({ ...variant, headline: e.target.value })}
          placeholder="Lê sem limites"
          className="bg-background border-border"
        />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Subtítulo (hero.subtitle)</Label>
        <Textarea
          value={variant.subtitle || ''}
          onChange={(e) => onChange({ ...variant, subtitle: e.target.value })}
          rows={2}
          placeholder="Acesso a milhares de contos..."
          className="bg-background border-border"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Texto CTA (hero.cta)</Label>
          <Input
            value={variant.cta_text || ''}
            onChange={(e) => onChange({ ...variant, cta_text: e.target.value })}
            placeholder="Subscrever"
            className="bg-background border-border"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Badge</Label>
          <Input
            value={variant.badge_text || ''}
            onChange={(e) => onChange({ ...variant, badge_text: e.target.value })}
            placeholder="Premium"
            className="bg-background border-border"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Plano destacado</Label>
          <Select
            value={variant.highlighted_plan_id || '__none__'}
            onValueChange={(v) => onChange({ ...variant, highlighted_plan_id: v === '__none__' ? '' : v })}
          >
            <SelectTrigger className="bg-background border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Nenhum —</SelectItem>
              {plans?.map((p) => (
                <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Tráfego (%)</Label>
          <Input
            type="number"
            min={0}
            max={100}
            value={variant.traffic_pct}
            onChange={(e) => onChange({ ...variant, traffic_pct: Number(e.target.value) || 0 })}
            className="bg-background border-border"
          />
        </div>
      </div>
    </div>
  );
}

function ResultsView({ results, winner, exp }) {
  if (!results) return null;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Resultados — {exp.name}</h3>
        <Badge variant="outline" className={`capitalize ${exp.status === 'active' ? 'border-emerald-400 text-emerald-400' : 'border-border'}`}>
          {exp.status}
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {results.map((r) => {
          const isWinner = winner === r.key;
          return (
            <Card key={r.key} className={`bg-surface border ${isWinner ? 'border-gold' : 'border-border'}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-lg font-bold text-foreground">Variante {r.key} · {r.name}</p>
                    {isWinner && (
                      <Badge className="mt-1 bg-gold text-black">
                        <Trophy className="w-3 h-3 mr-1" />
                        Vencedor preliminar
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-2 rounded bg-white/5">
                    <Eye className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Impressões</p>
                    <p className="text-xl font-bold text-foreground">{r.impressions}</p>
                  </div>
                  <div className="p-2 rounded bg-white/5">
                    <MousePointerClick className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Cliques</p>
                    <p className="text-xl font-bold text-foreground">{r.clicks}</p>
                  </div>
                  <div className="p-2 rounded bg-white/5">
                    <ShoppingCart className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Compras</p>
                    <p className="text-xl font-bold text-foreground">{r.conversions}</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <div>
                    <p className="text-muted-foreground">CTR</p>
                    <p className="font-semibold text-foreground">{r.ctr}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CVR</p>
                    <p className="font-semibold text-gold">{r.cvr}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Click → Buy</p>
                    <p className="font-semibold text-foreground">{r.click_to_buy}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {!winner && (
        <p className="text-xs text-muted-foreground text-center">
          Vencedor declarado apenas quando todas as variantes têm ≥30 impressões.
        </p>
      )}
    </div>
  );
}

export default function AdminExperiments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exps, setExps] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null); // experiment being edited or null
  const [editName, setEditName] = useState('');
  const [editVariants, setEditVariants] = useState([
    { ...EMPTY_VARIANT, key: 'A', name: 'Original' },
    { ...EMPTY_VARIANT, key: 'B', name: 'Variante' },
  ]);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState({}); // experiment_id -> {results, winner}
  const [showResults, setShowResults] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, plansRes] = await Promise.all([
        api.get('/admin/experiments'),
        api.get('/plans'),
      ]);
      setExps(expRes.data || []);
      const arr = Object.entries(plansRes.data || {}).map(([id, p]) => ({ ...p, plan_id: id }));
      setPlans(arr);
    } catch {
      toast.error('Erro a carregar experiências');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/home');
      return;
    }
    loadAll();
  }, [user, navigate, loadAll]);

  const openCreate = () => {
    setEditing({ id: null });
    setEditName('');
    setEditVariants([
      { ...EMPTY_VARIANT, key: 'A', name: 'Original' },
      { ...EMPTY_VARIANT, key: 'B', name: 'Variante' },
    ]);
  };

  const openEdit = (exp) => {
    setEditing(exp);
    setEditName(exp.name);
    setEditVariants(exp.variants.map((v) => ({ ...EMPTY_VARIANT, ...v })));
  };

  const save = async () => {
    if (!editName.trim()) {
      toast.error('Nome obrigatório');
      return;
    }
    const cleanVariants = editVariants.map(({ key, name, headline, subtitle, cta_text, badge_text, highlighted_plan_id, traffic_pct }) => ({
      key: (key || '').toUpperCase().trim(),
      name: name || '',
      headline: headline || null,
      subtitle: subtitle || null,
      cta_text: cta_text || null,
      badge_text: badge_text || null,
      highlighted_plan_id: highlighted_plan_id || null,
      traffic_pct: Number(traffic_pct) || 0,
    }));
    if (cleanVariants.some((v) => !v.key)) {
      toast.error('Todas as variantes precisam de uma key');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/experiments/${editing.id}`, { name: editName, variants: cleanVariants });
      } else {
        await api.post('/admin/experiments', { name: editName, variants: cleanVariants });
      }
      toast.success('Guardado');
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const start = async (exp) => {
    if (!confirm(`Ativar "${exp.name}"? Qualquer experiência ativa será arquivada.`)) return;
    try {
      await api.post(`/admin/experiments/${exp.id}/start`);
      toast.success('Experiência ativada');
      loadAll();
    } catch {
      toast.error('Erro ao ativar');
    }
  };

  const stop = async (exp) => {
    if (!confirm(`Parar "${exp.name}"?`)) return;
    try {
      await api.post(`/admin/experiments/${exp.id}/stop`);
      toast.success('Experiência arquivada');
      loadAll();
    } catch {
      toast.error('Erro ao parar');
    }
  };

  const remove = async (exp) => {
    if (!confirm(`Eliminar "${exp.name}" e todos os seus dados?`)) return;
    try {
      await api.delete(`/admin/experiments/${exp.id}`);
      toast.success('Eliminada');
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao eliminar');
    }
  };

  const viewResults = async (exp) => {
    try {
      const { data } = await api.get(`/admin/experiments/${exp.id}/results`);
      setResults((r) => ({ ...r, [exp.id]: data }));
      setShowResults(exp.id);
    } catch {
      toast.error('Erro ao carregar resultados');
    }
  };

  const updateVariant = (idx, v) => {
    const copy = [...editVariants];
    copy[idx] = v;
    setEditVariants(copy);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <FlaskConical className="w-7 h-7 text-ruby" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">A/B Testing</h1>
              <p className="text-muted-foreground text-sm">
                Testa copy, badges e plano destacado para maximizar conversão Premium.
              </p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-ruby hover:bg-ruby-light" data-testid="btn-new-experiment">
            <Plus className="w-4 h-4 mr-2" />
            Nova Experiência
          </Button>
        </div>

        {exps.length === 0 && (
          <Card className="bg-surface border-border">
            <CardContent className="p-12 text-center">
              <FlaskConical className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-semibold">Ainda não tens experiências.</p>
              <p className="text-muted-foreground text-sm mt-1">Cria a tua primeira para começar a otimizar a página Premium.</p>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 gap-4">
          {exps.map((exp) => (
            <Card key={exp.id} className="bg-surface border-border" data-testid={`experiment-${exp.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      {exp.name}
                      <Badge
                        variant="outline"
                        className={`capitalize ${
                          exp.status === 'active' ? 'border-emerald-400 text-emerald-400' : 'border-border'
                        }`}
                      >
                        {exp.status}
                      </Badge>
                    </CardTitle>
                    <CardDescription>{exp.variants?.length || 0} variantes • criada por {exp.created_by}</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => viewResults(exp)} data-testid={`btn-results-${exp.id}`}>
                      <BarChart3 className="w-4 h-4 mr-1" />
                      Resultados
                    </Button>
                    {exp.status !== 'active' && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(exp)} data-testid={`btn-edit-${exp.id}`}>
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                    )}
                    {exp.status === 'active' ? (
                      <Button size="sm" variant="outline" onClick={() => stop(exp)} data-testid={`btn-stop-${exp.id}`}>
                        <Pause className="w-4 h-4 mr-1" />
                        Parar
                      </Button>
                    ) : (
                      <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600" onClick={() => start(exp)} data-testid={`btn-start-${exp.id}`}>
                        <Play className="w-4 h-4 mr-1" />
                        Ativar
                      </Button>
                    )}
                    {exp.status !== 'active' && (
                      <Button size="sm" variant="outline" onClick={() => remove(exp)} className="text-ruby border-ruby/30 hover:bg-ruby/10" data-testid={`btn-delete-${exp.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {exp.variants.map((v) => (
                    <div key={v.key} className="p-3 bg-white/5 rounded-md">
                      <p className="font-semibold text-foreground text-sm">{v.key} · {v.name}</p>
                      <p className="text-xs text-muted-foreground mt-1 truncate">{v.headline || '—'}</p>
                      <p className="text-xs text-muted-foreground">{v.traffic_pct}% do tráfego</p>
                    </div>
                  ))}
                </div>
                {showResults === exp.id && results[exp.id] && (
                  <div className="mt-5 pt-5 border-t border-border" data-testid={`results-${exp.id}`}>
                    <ResultsView results={results[exp.id].results} winner={results[exp.id].winner_variant_key} exp={exp} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
          <DialogContent className="bg-surface border-border max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                {editing?.id ? 'Editar Experiência' : 'Nova Experiência A/B'}
              </DialogTitle>
              <DialogDescription>
                Cria 2 ou mais variantes. Apenas uma experiência pode estar ativa em simultâneo.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-foreground">Nome da experiência</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Ex: Teste novo headline vs. original"
                  className="bg-background border-border"
                  data-testid="input-exp-name"
                />
              </div>
              {editVariants.map((v, i) => (
                <VariantEditor
                  key={i}
                  variant={v}
                  onChange={(nv) => updateVariant(i, nv)}
                  plans={plans}
                />
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-ruby hover:bg-ruby-light" data-testid="btn-save-experiment">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
